import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, Check, ExternalLink, ShieldCheck, X } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  businessKybApi,
  type KybTin,
  labelFor,
  CHECK_LABEL,
  DOCUMENT_LABEL,
  KybCheck,
  KybDetail,
  KybDocument,
} from '../../api/business-kyb';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { providerName } from '../../components/ui/BvnProviders';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';

/** The server's floor on an override reason, mirrored so the button can
 *  explain itself before the request rather than after. */
const MIN_REASON = 10;

const CHECK_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> =
  {
    verified: 'success',
    failed: 'danger',
    expired: 'warning',
    pending: 'warning',
    missing: 'neutral',
  };

const DOC_TONE: Record<string, 'success' | 'danger' | 'warning'> = {
  approved: 'success',
  rejected: 'danger',
  pending: 'warning',
};

/**
 * Reviewing one business's verification.
 *
 * This page did not exist, which is why uploaded documents could not be seen
 * and a failed liveness check had nowhere to go — the endpoints behind it were
 * all reachable by API and by nothing else.
 *
 * Two authorities meet here and are deliberately not the same thing: reviewing
 * paperwork (`business_kyb.review`) and overruling an identity provider
 * (`business_kyb.override`). The second is what ultimately lets a business take
 * money from strangers.
 */
export const BusinessKyb: React.FC = () => {
  const { businessId = '' } = useParams();
  usePage('Business Verification');
  const toast = useToast();

  const [detail, setDetail] = useState<KybDetail | null>(null);
  const [documents, setDocuments] = useState<KybDocument[]>([]);
  const [checks, setChecks] = useState<KybCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [override, setOverride] = useState<KybCheck | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  /** The document being rejected, while its reason is written. */
  /** What is being rejected, while its reason is written: a document, or
   *  the TIN. */
  const [rejecting, setRejecting] = useState<KybDocument | 'tin' | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const [d, docs, chks] = await Promise.all([
        businessKybApi.detail(businessId),
        businessKybApi.documents(businessId),
        businessKybApi.checks(businessId),
      ]);
      setDetail(d);
      setDocuments(docs);
      setChecks(chks);
    } catch (err) {
      toast.error(err, 'Could not load this business.');
    } finally {
      setLoading(false);
    }
  }, [businessId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Fetched on demand, because opening a document is recorded against the
   *  reviewer who did it. */
  const openDocument = async (doc: KybDocument) => {
    try {
      const { url } = await businessKybApi.documentFile(businessId, doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err, 'Could not open that document.');
    }
  };

  const reviewDocument = async (
    doc: KybDocument,
    approved: boolean,
    reason?: string,
  ) => {
    try {
      await businessKybApi.reviewDocument(businessId, doc.id, approved, reason);
      toast.success(`${labelFor(DOCUMENT_LABEL, doc.kind)} ${approved ? 'approved' : 'rejected'}`);
      await load();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    }
  };

  const reviewTin = async (approved: boolean, reason?: string) => {
    try {
      await businessKybApi.reviewTin(businessId, approved, reason);
      toast.success(`TIN ${approved ? 'approved' : 'rejected'}`);
      await load();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    }
  };

  const submitOverride = async (verified: boolean) => {
    if (!override || reason.trim().length < MIN_REASON) return;
    setSaving(true);
    try {
      const { tier } = await businessKybApi.overrideCheck(
        businessId,
        override.id,
        verified,
        reason.trim(),
      );
      toast.success(
        verified
          ? `Check cleared. Business is now tier ${tier}.`
          : 'Check marked as failed.',
      );
      setOverride(null);
      setReason('');
      await load();
    } catch (err) {
      toast.error(err, 'Could not override that check.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  const reasonTooShort = reason.trim().length < MIN_REASON;

  return (
    <div>
      <PageHeader
        title="Business Verification"
        description="Documents, identity checks, and what is holding the tier back"
      />

      {detail ? (
        <Card>
          <div
            style={{
              display: 'flex',
              gap: 32,
              flexWrap: 'wrap',
              padding: 20,
              alignItems: 'center',
            }}
          >
            <Figure label="KYB status" value={detail.kybStatus} />
            <Figure label="Tier" value={`Tier ${detail.currentTier}`} />
            {/* Surfaced rather than hidden. When the cached tier and the
                derived one disagree, the derived one is right and the cache
                is stale — showing it is what makes drift findable. */}
            {detail.tierIsStale ? (
              <Badge variant="warning">
                Cached tier {detail.storedTier} is stale
              </Badge>
            ) : null}
          </div>

          {detail.outstanding.length > 0 ? (
            <div
              style={{
                borderTop: '1px solid var(--neutral-300)',
                padding: 20,
              }}
            >
              <h2 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 10px' }}>
                Outstanding
              </h2>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {detail.outstanding.map((o, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    {o.requirement}
                    {o.detail ? (
                      <span style={{ color: 'var(--neutral-500)' }}> — {o.detail}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div style={{ height: 20 }} />

      <Card>
        <SectionHeader title="Identity checks" />
        {checks.length === 0 ? (
          <Empty>No checks recorded for this business.</Empty>
        ) : (
          checks.map((check) => (
            <Row key={check.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {labelFor(CHECK_LABEL, check.checkType)}
                  <span style={{ color: 'var(--neutral-500)', fontWeight: 400 }}>
                    {' '}· {check.directorName}
                  </span>
                </div>
                {check.failureReason ? (
                  <div style={{ fontSize: 12, color: 'var(--neutral-600)', marginTop: 4 }}>
                    {check.failureReason}
                  </div>
                ) : null}

                {/*
                  The face the check was made on.

                  Shown rather than linked, because a reviewer comparing it to
                  a name and a BVN should not have to open a tab to do it —
                  and shown on a failed check as much as a passed one, since
                  the failed row is the one that reaches a queue and the
                  photo is usually the whole story.
                */}
                {check.fileUrl ? (
                  <a
                    href={check.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-block', marginTop: 8 }}
                  >
                    <img
                      src={check.fileUrl}
                      alt={`Liveness selfie for ${check.directorName}`}
                      style={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--neutral-300)',
                        display: 'block',
                      }}
                    />
                  </a>
                ) : null}
                {/* Only ever set by a human override, so it is worth saying
                    out loud — "a person decided" is not "the provider
                    verified this". */}
                {/* Which service answered — Dojah, Interswitch — so a
                    reviewer knows whose verdict they are reading. */}
                {check.provider && check.provider !== 'manual' ? (
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 4 }}>
                    Checked with {providerName(check.provider)}
                  </div>
                ) : null}
                {check.provider === 'manual' ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--neutral-500)',
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <ShieldCheck size={13} />
                    Decided by a reviewer, not by a provider
                  </div>
                ) : null}
              </div>

              <Badge variant={CHECK_TONE[check.status] ?? 'neutral'}>
                {check.status}
              </Badge>

              <Button
                variant="secondary"
                onClick={() => {
                  setOverride(check);
                  setReason('');
                }}
              >
                Override
              </Button>
            </Row>
          ))
        )}
      </Card>

      <div style={{ height: 20 }} />

      {detail?.tin ? (
        <>
          <Card>
            <SectionHeader title="Tax Identification Number" />
            <TinReview
              tin={detail.tin}
              onApprove={() => void reviewTin(true)}
              onReject={() => {
                setRejectReason('');
                setRejecting('tin');
              }}
            />
          </Card>
          <div style={{ height: 20 }} />
        </>
      ) : null}

      <Card>
        <SectionHeader title="Documents" />
        {documents.length === 0 ? (
          <Empty>Nothing uploaded yet.</Empty>
        ) : (
          documents.map((doc) => (
            <Row key={doc.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {labelFor(DOCUMENT_LABEL, doc.kind)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 4 }}>
                  {doc.fileName ?? 'Unnamed file'} ·{' '}
                  {new Date(doc.uploadedAt).toLocaleDateString()}
                </div>
                {doc.kind === 'cac' ? <CertificateNumberCheck doc={doc} /> : null}
                {doc.rejectionReason ? (
                  <div style={{ fontSize: 12, color: 'var(--danger-900)', marginTop: 4 }}>
                    {doc.rejectionReason}
                  </div>
                ) : null}
              </div>

              {doc.status === 'pending' &&
              doc.registrationReviewRequestedAt &&
              !doc.registrationMatchedAt ? (
                <Badge variant="warning">Manual check requested</Badge>
              ) : null}
              <Badge variant={DOC_TONE[doc.status] ?? 'warning'}>{doc.status}</Badge>

              <Button variant="secondary" onClick={() => void openDocument(doc)}>
                <ExternalLink size={14} />
                View
              </Button>

              {doc.status === 'pending' ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      // The business is shown this, so it is asked for.
                      setRejectReason('');
                      setRejecting(doc);
                    }}
                  >
                    <X size={14} />
                    Reject
                  </Button>
                  <Button onClick={() => void reviewDocument(doc, true)}>
                    <Check size={14} />
                    Approve
                  </Button>
                </>
              ) : null}
            </Row>
          ))
        )}
      </Card>

      <Modal
        isOpen={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={
          rejecting === 'tin'
            ? 'Reject the TIN'
            : rejecting
              ? `Reject ${labelFor(DOCUMENT_LABEL, rejecting.kind)}`
              : ''
        }
        maxWidth={520}
      >
        {rejecting ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Why? The business sees this.
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="e.g. This is the certificate for a different company. Upload the one for RC 1849256."
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setRejecting(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={rejectReason.trim().length < MIN_REASON}
                onClick={() => {
                  const target = rejecting;
                  setRejecting(null);
                  if (target === 'tin') {
                    void reviewTin(false, rejectReason.trim());
                  } else {
                    void reviewDocument(target, false, rejectReason.trim());
                  }
                }}
              >
                {rejectReason.trim().length < MIN_REASON ? 'Add a reason first' : 'Reject'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={override !== null}
        onClose={() => setOverride(null)}
        title={
          override
            ? `Override ${labelFor(CHECK_LABEL, override.checkType)}`
            : ''
        }
        maxWidth={560}
      >
        {override ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div
              style={{
                display: 'flex',
                gap: 10,
                padding: 12,
                borderRadius: 'var(--radius-md)',
                background: 'var(--warning-100, #FDF3E2)',
                color: 'var(--warning-900, #A86A12)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                This overrules an automated identity decision on{' '}
                <strong>{override.directorName}</strong>. Clearing it can raise
                the business&apos;s tier and let it take money from customers.
              </span>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                What did you check?
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Passport and utility bill checked against the CAC filing by hand."
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  resize: 'vertical',
                }}
              />
              <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 4 }}>
                Kept on the record. In a dispute months from now this is the
                only account of why the provider was overruled.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button
                variant="danger"
                disabled={saving || reasonTooShort}
                onClick={() => void submitOverride(false)}
              >
                Mark failed
              </Button>
              <Button
                disabled={saving || reasonTooShort}
                onClick={() => void submitOverride(true)}
              >
                {reasonTooShort ? 'Add a reason first' : 'Clear this check'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
  </div>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '14px 20px',
      borderBottom: '1px solid var(--neutral-200)',
      flexWrap: 'wrap',
    }}
  >
    {children}
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: 28, textAlign: 'center', color: 'var(--neutral-500)', fontSize: 14 }}>
    {children}
  </div>
);

const Figure: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, textTransform: 'capitalize' }}>
      {value}
    </div>
  </div>
);

/**
 * What the machine made of a CAC certificate's number.
 *
 * The CAC step counts a certificate once its printed number matches the
 * verified registration. When it could not be matched — nothing readable,
 * or a photo OCR may have misread — the business can ask for this check,
 * and approving the certificate is what counts as the match. So the reviewer
 * is told what was read, and how, before deciding.
 */
function CertificateNumberCheck({ doc }: { doc: KybDocument }) {
  const read = doc.readRegistrationNumber;
  const how =
    doc.registrationReadSource === 'pdf-text'
      ? 'from the PDF text'
      : doc.registrationReadSource === 'ocr'
        ? 'from a photo (OCR)'
        : null;
  const text = doc.registrationMatchedAt
    ? `Number matched the verified registration${read ? ` (${read})` : ''}.`
    : read
      ? `Read ${read} ${how ?? ''} — doesn't match the verified registration. Compare it by eye before approving.`
      : doc.registrationReadSource
        ? 'No registration number could be read. Compare the certificate to the business\'s RC number before approving.'
        : null;
  if (!text) return null;
  return (
    <div
      style={{
        fontSize: 12,
        marginTop: 4,
        color: doc.registrationMatchedAt ? 'var(--success-900, #1E7B45)' : 'var(--warning-900, #A86A12)',
      }}
    >
      {text}
    </div>
  );
}

/**
 * The TIN, beside what the tax registry holds for the business's RC number.
 *
 * A TIN that matched the registry is verified on the spot and needs nothing
 * here. What reaches a reviewer is one the registry disagreed with or had
 * no record of, which the business asked a person to check — so the two
 * numbers are shown together before anybody decides.
 */
function TinReview({
  tin,
  onApprove,
  onReject,
}: {
  tin: KybTin;
  onApprove: () => void;
  onReject: () => void;
}) {
  const state = tin.verifiedAt
    ? { label: 'Verified', tone: 'success' as const }
    : tin.reviewRequested
      ? { label: 'Manual check requested', tone: 'warning' as const }
      : tin.rejectionReason
        ? { label: 'Rejected', tone: 'danger' as const }
        : { label: tin.tin ? 'Not verified' : 'Not given', tone: 'warning' as const };

  return (
    <Row>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{tin.tin ?? '—'}</div>
        <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 4 }}>
          {tin.registryValue
            ? `The registry holds ${tin.registryValue} for this RC number.`
            : tin.reviewRequested
              ? 'The registry holds no TIN for this RC number.'
              : null}
        </div>
        {tin.rejectionReason ? (
          <div style={{ fontSize: 12, color: 'var(--danger-900)', marginTop: 4 }}>
            {tin.rejectionReason}
          </div>
        ) : null}
      </div>

      <Badge variant={state.tone}>{state.label}</Badge>

      {tin.reviewRequested ? (
        <>
          <Button variant="secondary" onClick={onReject}>
            <X size={14} />
            Reject
          </Button>
          <Button onClick={onApprove}>
            <Check size={14} />
            Approve
          </Button>
        </>
      ) : null}
    </Row>
  );
}
